import { useState } from "react";
import { Check, Loader2 } from "lucide-react";
import { z } from "zod";
import { supabase } from "@/integrations/supabase/client";
import { toast } from "@/hooks/use-toast";
import { fireBlueConfetti } from "@/lib/blueConfetti";
import { trackEvent } from "@/utils/mixpanel";
import { pushDataLayerEvent } from "@/utils/googleAnalytics";
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogHeader,
  DialogTitle,
  DialogTrigger,
} from "@/components/ui/dialog";

const emailSchema = z
  .string()
  .trim()
  .email({ message: "Enter a valid email address" })
  .max(255, { message: "Email must be less than 255 characters" });

function WindowsLogo({ className }: { className?: string }) {
  return (
    <svg viewBox="0 0 24 24" fill="currentColor" aria-hidden="true" className={className}>
      <path d="M3 5.4 10.2 4.4v7.1H3V5.4Zm0 13.2 7.2 1v-7H3v6ZM11.3 4.2 21 3v8.5h-9.7V4.2Zm0 8.4H21V21l-9.7-1.3v-7.1Z" />
    </svg>
  );
}

interface Props {
  /** Extra classes for the trigger button (defaults match the macOS button). */
  className?: string;
  iconClassName?: string;
}

export default function WindowsWaitlistButton({
  className = "btn-gamey w-full sm:w-auto justify-center inline-flex items-center gap-2 sm:gap-2.5 bg-white hover:bg-slate-50 text-[#0B6BB0] font-bold uppercase text-[13px] sm:text-sm xl:text-base tracking-wide px-6 py-3 sm:px-8 sm:py-4 xl:px-9 xl:py-[1.15rem] rounded-2xl transition-all opacity-60 hover:opacity-100",
  iconClassName = "w-4 h-4 xl:w-5 xl:h-5",
}: Props) {
  const [open, setOpen] = useState(false);
  const [email, setEmail] = useState("");
  const [submitting, setSubmitting] = useState(false);
  const [done, setDone] = useState(false);

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    const parsed = emailSchema.safeParse(email);
    if (!parsed.success) {
      toast({
        title: "Check your email",
        description: parsed.error.issues[0].message,
        variant: "destructive",
      });
      return;
    }
    setSubmitting(true);
    const { error } = await supabase
      .from("waitlist_signups")
      .insert({ email: parsed.data.toLowerCase(), platform: "windows" });
    setSubmitting(false);
    if (error) {
      toast({
        title: "Something went wrong",
        description: "Please try again in a moment.",
        variant: "destructive",
      });
      return;
    }
    try {
      trackEvent("windows_waitlist_joined", { platform: "windows" });
      // Its OWN GTM event — deliberately NOT email_captured. That event is
      // mapped to the Google Ads "Account created" conversion, and a Windows
      // waitlist signup is not an account; reusing it inflated signups with
      // people who cannot even run the product yet.
      pushDataLayerEvent("windows_waitlist_joined", {
        source: "windows_waitlist",
        platform: "windows",
      });
    } catch {
      /* analytics must never break the form */
    }
    setDone(true);
    fireBlueConfetti();
  };

  return (
    <Dialog
      open={open}
      onOpenChange={(v) => {
        setOpen(v);
        if (!v) {
          setDone(false);
          setEmail("");
        }
      }}
    >
      <DialogTrigger asChild>
        <button
          type="button"
          style={{
            ["--gamey-shadow" as string]: "#bcdcf2",
            ["--gamey-shadow-hover" as string]: "#9cc8ea",
          }}
          className={className}
        >
          <WindowsLogo className={iconClassName} />
          Windows waitlist
        </button>
      </DialogTrigger>

      <DialogContent className="sm:max-w-md rounded-3xl">
        {done ? (
          <div className="text-center py-4">
            <div className="w-14 h-14 mx-auto mb-4 rounded-2xl grid place-items-center text-white bg-gradient-to-br from-sky-500 to-blue-600">
              <Check className="w-7 h-7" strokeWidth={3} />
            </div>
            <DialogHeader>
              <DialogTitle className="text-center">You're on the list</DialogTitle>
              <DialogDescription className="text-center">
                We'll email you the moment Vovy for Windows is ready.
              </DialogDescription>
            </DialogHeader>
          </div>
        ) : (
          <>
            <DialogHeader>
              <DialogTitle>Vovy for Windows</DialogTitle>
              <DialogDescription>
                It's coming. Drop your email and we'll tell you first.
              </DialogDescription>
            </DialogHeader>
            <form onSubmit={handleSubmit} className="mt-2 space-y-3">
              <input
                type="email"
                required
                maxLength={255}
                value={email}
                onChange={(e) => setEmail(e.target.value)}
                placeholder="you@example.com"
                className="w-full rounded-2xl border border-border bg-background px-4 py-3 text-base outline-none focus:ring-2 focus:ring-sky-400"
              />
              <button
                type="submit"
                disabled={submitting}
                style={{
                  ["--gamey-shadow" as string]: "#0369a1",
                  ["--gamey-shadow-hover" as string]: "#075985",
                }}
                className="btn-gamey w-full inline-flex items-center justify-center gap-2 rounded-2xl bg-[#00A3FF] px-6 py-3.5 text-sm font-bold uppercase tracking-wide text-white transition-colors hover:bg-[#00A3FF]/90 disabled:opacity-60"
              >
                {submitting && <Loader2 className="w-4 h-4 animate-spin" />}
                Join the waitlist
              </button>
            </form>
          </>
        )}
      </DialogContent>
    </Dialog>
  );
}
